import { parse } from 'graphql'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'

/**
 * Kept as a parsed DocumentNode (not codegen `graphql()`) so we can ship
 * FormColumnData / HtmlColumnData fields before regenerating against a live schema.
 * Re-run `npm run codegen` once the shop-api includes these types, then switch back if desired.
 */
export const GET_PAGE_QUERY = parse(`
  query GetPage($slug: String, $id: ID) {
    getPage(slug: $slug, id: $id) {
      id
      title
      slug
      active
      blocks {
        id
        index
        code
        active
        css
        columns {
          type
          label
          css
          data {
            ... on VideoColumnData {
              type
              label
              url
              asset {
                preview
                source
              }
            }
            ... on ImageColumnData {
              type
              label
              title
              subtitle
              button {
                label
                type
                openInNewTab
                url
                linkRef
              }
              asset {
                preview
                source
              }
            }
            ... on CtaColumnData {
              type
              title
              description
              button {
                label
                type
                openInNewTab
                url
                linkRef
              }
            }
            ... on ContentColumnData {
              type
              description
            }
            ... on StatsColumnData {
              type
              items {
                label
                value
              }
            }
            ... on BannerColumnData {
              type
              banner {
                id
                title
                active
                items {
                  name
                  description
                  index
                  asset {
                    preview
                  }
                  button {
                    label
                    type
                  }
                }
              }
            }
            ... on CollectionsColumnData {
              type
              filters {
                combinator
                conditions {
                  field
                  operator
                  value
                }
              }
            }
            ... on KitsColumnData {
              type
              filters {
                combinator
                conditions {
                  field
                  operator
                  value
                }
              }
            }
            ... on ProductVariantsColumnData {
              type
              filters {
                combinator
                conditions {
                  field
                  operator
                  value
                }
              }
            }
            ... on FaqColumnData {
              type
              faq {
                id
                title
                active
                items {
                  question
                  explanation
                  index
                }
              }
            }
            ... on DocumentColumnData {
              type
              document {
                id
                title
                active
                items {
                  name
                  index
                  asset {
                    preview
                    source
                  }
                }
              }
            }
            ... on NewsColumnData {
              type
              items {
                id
                title
                description
                src
                asset {
                  preview
                }
              }
            }
            ... on EventColumnData {
              type
              itemId
              event {
                id
                title
                slug
                startDate
                endDate
                asset {
                  preview
                }
              }
            }
            ... on FormColumnData {
              type
              submitLabel
              submitEndpoint
              customHtml
              fields {
                key
                label
                fieldType
                width
                required
                placeholder
                options {
                  label
                  value
                }
              }
            }
            ... on HtmlColumnData {
              type
              html
            }
          }
        }
      }
      seo {
        title
        description
      }
    }
  }
`) as TypedDocumentNode<
  {
    getPage?: {
      id: string
      title: string
      slug?: string | null
      active: boolean
      blocks: Array<{
        id: string
        index: number
        code?: string | null
        active: boolean
        css?: Record<string, unknown> | null
        columns: Array<{
          type: string
          label?: string | null
          css: Record<string, unknown>
          data: Record<string, unknown> | null
        }>
      }>
      seo?: { title?: string | null; description?: string | null } | null
    } | null
  },
  { slug?: string | null; id?: string | null }
>
